import { useEffect, useMemo, useState } from 'react';
import { productMasterAPI } from '../services/api';
import { balancePhrases, DEFAULT_BALANCE_LABEL, type BalancePhrases } from '../utils/balanceLabel';

/**
 * Every phrase naming what a project counts, for any screen that needs one.
 *
 * WHY A HOOK AND NOT PROP-DRILLING
 * The failure mode of a per-project label is not that it breaks — it is that a
 * new screen hard-codes "stock" because threading the label down to it was more
 * work than typing the word. So getting it right has to be the path of least
 * resistance: one line, no props, no fetch to wire up.
 *
 *     const phrases = useBalanceLabel(projectId);
 *     <h2>{phrases.byStore}</h2>
 *
 * CACHED per project at module scope. The label changes about once in a
 * project's life, and several components on one page ask for it independently —
 * ProductStockPanel, the tab strip above it, the panel heading above that — so
 * without this they would each fire the same request on every mount.
 *
 * Renders with 'Stock' until the fetch lands. That is deliberate: a heading that
 * flickers from blank to a word is worse than one that corrects itself, and for
 * the vast majority of projects the default is already right.
 */
const cache = new Map<number, string>();
const inFlight = new Map<number, Promise<string>>();

const fetchLabel = (projectId: number): Promise<string> => {
  const cached = cache.get(projectId);
  if (cached) return Promise.resolve(cached);

  const running = inFlight.get(projectId);
  if (running) return running;

  const p = productMasterAPI
    .getBalanceLabel(projectId)
    .then((res) => {
      const label = String(res.data?.data?.label ?? '') || DEFAULT_BALANCE_LABEL;
      cache.set(projectId, label);
      return label;
    })
    // A heading is not worth an error state. Fall back to the default and let
    // the page render.
    .catch(() => DEFAULT_BALANCE_LABEL)
    .finally(() => { inFlight.delete(projectId); });

  inFlight.set(projectId, p);
  return p;
};

/** Call after saving a new label, so other screens do not serve the old word. */
export const invalidateBalanceLabel = (projectId: number): void => {
  cache.delete(projectId);
};

export const useBalanceLabel = (projectId: number | null | undefined): BalancePhrases => {
  const [label, setLabel] = useState<string>(
    () => (projectId ? cache.get(projectId) : undefined) ?? DEFAULT_BALANCE_LABEL,
  );

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    fetchLabel(projectId).then((l) => { if (alive) setLabel(l); });
    return () => { alive = false; };
  }, [projectId]);

  return useMemo(() => balancePhrases(label), [label]);
};
